import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const STATUS = {
  aguardando_nf: { l: "Aguardando NF", c: "bg-[#FFCC00]" },
  pendente: { l: "Aguardando NF", c: "bg-[#FFCC00]" }, // legacy
  nf_recebida: { l: "Em análise", c: "bg-[#142766] text-white" },
  emitida: { l: "Em análise", c: "bg-[#142766] text-white" }, // legacy
  nf_validada: { l: "Validada · aguardando pagamento", c: "bg-[#00A859] text-white" },
  nf_rejeitada: { l: "Rejeitada · reenvie", c: "bg-[#E60000] text-white" },
  nf_paga: { l: "Pago", c: "bg-foreground text-white" },
  atrasada: { l: "Atrasada", c: "bg-[#E60000] text-white" },
  acumulada: { l: "Acumulada", c: "bg-[#142766] text-white" },
};

const API = process.env.REACT_APP_BACKEND_URL;

export default function MontadorNotasFiscais() {
  const [pends, setPends] = useState([]);
  // Estado por id para evitar sobrescrever entre múltiplas pendências
  const [forms, setForms] = useState({}); // { [pid]: {nf_numero, arquivo} }
  const [acoes, setAcoes] = useState({}); // { [pid]: {dando_ciencia, anexando} }

  const load = async () => {
    try {
      const r = await api.get("/montador/pendencias-nf");
      setPends(r.data);
    } catch (e) { /* silent */ }
  };

  useEffect(() => { load(); }, []);

  const setForm = (pid, patch) => setForms(prev => ({ ...prev, [pid]: { ...(prev[pid] || {}), ...patch } }));
  const setAcao = (pid, patch) => setAcoes(prev => ({ ...prev, [pid]: { ...(prev[pid] || {}), ...patch } }));

  const darCiencia = async (pid) => {
    setAcao(pid, { dando_ciencia: true });
    try {
      await api.post(`/montador/pendencias-nf/${pid}/ciencia`);
      toast.success("Ciência registrada (auditada juridicamente)");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAcao(pid, { dando_ciencia: false }); }
  };

  const anexar = async (pid) => {
    const f = forms[pid] || {};
    if (!f.arquivo || !(f.nf_numero || "").trim()) {
      toast.error("Informe o nº da NF e selecione o arquivo"); return;
    }
    setAcao(pid, { anexando: true });
    try {
      const fd = new FormData();
      fd.append("arquivo", f.arquivo);
      fd.append("nf_numero", f.nf_numero);
      const r = await api.post(`/montador/pendencias-nf/${pid}/anexar`, fd);
      toast.success(r.data.status === "acumulada"
        ? `NF anexada · valor acumulado para ${r.data.acumulada_para_periodo}`
        : "NF anexada com sucesso");
      setForm(pid, { nf_numero: "", arquivo: null });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAcao(pid, { anexando: false }); }
  };

  const pendentesCount = pends.filter(p => ["pendente","aguardando_nf","atrasada","nf_rejeitada"].includes(p.status)).length;
  const emitidasCount = pends.filter(p => ["emitida","nf_recebida","nf_validada","acumulada"].includes(p.status)).length;
  const pagasCount = pends.filter(p => p.status === "nf_paga").length;

  const baixarComprovante = async (pid, empresaId) => {
    const token = localStorage.getItem("token");
    const url = `${API}/api/empresas/${empresaId}/pendencias-nf/${pid}/comprovante`;
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) { toast.error("Erro no download"); return; }
    const blob = await r.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `Comprovante_${pid}.pdf`;
    a.click(); URL.revokeObjectURL(a.href);
  };

  return (
    <>
      <div className="max-w-4xl mx-auto p-6 space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div className="border border-border bg-white p-4">
            <div className="label-cap">Pendentes</div>
            <div className="font-heading font-extrabold text-3xl text-[#E60000]">{pendentesCount}</div>
          </div>
          <div className="border border-border bg-white p-4">
            <div className="label-cap">Em processo</div>
            <div className="font-heading font-extrabold text-3xl text-[#142766]">{emitidasCount}</div>
          </div>
          <div className="border border-border bg-white p-4">
            <div className="label-cap">Pagas</div>
            <div className="font-heading font-extrabold text-3xl text-[#00A859]">{pagasCount}</div>
          </div>
        </div>

        {pends.length === 0 && (
          <div className="border border-dashed border-border p-12 text-center text-sm text-muted-foreground">
            Você não possui pendências de NF no momento. 🎉
          </div>
        )}

        {pends.map(p => {
          const st = STATUS[p.status] || STATUS.aguardando_nf;
          const f = forms[p.id] || {};
          const a = acoes[p.id] || {};
          const podeCiencia = !p.ciencia_dada_em;
          const isRejeitada = p.status === "nf_rejeitada";
          // Rejeitada permite reenvio (precisa anexar nova NF). Aguardando/emitida sem arquivo também
          const podeAnexar = (p.ciencia_dada_em && !p.nf_arquivo_path) || isRejeitada;
          const finalizada = !!p.nf_arquivo_path && !isRejeitada;
          const isPaga = p.status === "nf_paga";
          return (
            <div key={p.id} className="border-2 border-border bg-white p-5" data-testid={`mnf-${p.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="label-cap text-[#142766]">{p.empresa_nome}</div>
                  <div className="font-heading font-extrabold text-xl mt-1">Período {p.periodo}</div>
                  <div className="text-sm text-muted-foreground">
                    Valor: <strong className="font-mono text-foreground">R$ {p.valor?.toFixed(2)}</strong>
                  </div>
                  <div className="text-xs mt-1">
                    Vencimento: <strong>{new Date(p.vencimento).toLocaleDateString("pt-BR")}</strong>
                  </div>
                  {["pendente", "aguardando_nf", "atrasada"].includes(p.status) && (
                    <div className="text-[11px] text-amber-700 mt-0.5" data-testid={`mnf-prazo-${p.id}`}>
                      Emita até o vencimento — após o prazo o valor acumula para o próximo fechamento.
                    </div>
                  )}
                </div>
                <span className={`px-2 py-1 text-[10px] uppercase tracking-wider ${st.c}`}>{st.l}</span>
              </div>

              {p.ciencia_dada_em && (
                <div className="text-[10px] text-[#00A859] mb-2">
                  ✓ Ciência registrada em {new Date(p.ciencia_dada_em).toLocaleString("pt-BR")}
                </div>
              )}

              {isRejeitada && p.motivo_rejeicao && (
                <div className="border-l-4 border-[#E60000] bg-red-50 p-3 text-xs mb-3" data-testid={`mnf-motivo-${p.id}`}>
                  <strong>Motivo da rejeição:</strong> {p.motivo_rejeicao}
                  <div className="mt-1 text-[10px] text-muted-foreground">
                    Rejeitada em {new Date(p.rejeitada_em).toLocaleString("pt-BR")} · por {p.rejeitada_por}
                  </div>
                  <div className="mt-2 text-[11px]">⚠️ Corrija e reenvie a NF abaixo.</div>
                </div>
              )}

              {finalizada && !isPaga && (
                <div className="border-l-4 border-[#00A859] bg-[#00A859]/5 p-3 text-xs">
                  NF nº <strong className="font-mono">{p.nf_numero}</strong> anexada em {new Date(p.nf_emitida_em).toLocaleString("pt-BR")}
                  {p.status === "acumulada" && p.acumulada_para_periodo && (
                    <div className="mt-1 text-amber-700">⚠️ Valor acumulado para {p.acumulada_para_periodo}</div>
                  )}
                  {p.status === "nf_validada" && (
                    <div className="mt-1 text-[#00A859] font-bold">✓ Validada · aguardando pagamento</div>
                  )}
                </div>
              )}

              {isPaga && (
                <div className="border-l-4 border-foreground bg-foreground/5 p-3 text-xs space-y-1">
                  <div>
                    <strong>Pagamento confirmado:</strong> R$ {p.valor_pago?.toFixed(2)} em {p.data_pagamento && new Date(p.data_pagamento).toLocaleDateString("pt-BR")}
                  </div>
                  {p.comprovante_path && (
                    <Button size="sm" variant="outline" className="btn-sharp h-7 text-[10px] mt-1"
                      onClick={() => baixarComprovante(p.id, p.empresa_id)}
                      data-testid={`mnf-comprovante-${p.id}`}>
                      📎 Baixar comprovante
                    </Button>
                  )}
                </div>
              )}

              {podeCiencia && (
                <Button onClick={() => darCiencia(p.id)} disabled={a.dando_ciencia}
                  className="w-full mt-3 h-12 btn-sharp brand-bg text-white" data-testid={`mnf-ciencia-${p.id}`}>
                  {a.dando_ciencia ? "Registrando…" : "✓ Estou Ciente"}
                </Button>
              )}

              {podeAnexar && (
                <div className="mt-3 border-t border-border pt-3">
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">{isRejeitada ? "Novo nº da NF" : "Nº da Nota Fiscal"}</Label>
                      <Input value={f.nf_numero || ""} onChange={(e) => setForm(p.id, { nf_numero: e.target.value })}
                        placeholder="Ex: 000123" className="mt-1" data-testid={`mnf-numero-${p.id}`} />
                    </div>
                    <div>
                      <Label className="text-xs">Arquivo (PDF/JPG/PNG · 10MB)</Label>
                      <Input type="file" accept=".pdf,.jpg,.jpeg,.jfif,.png,.webp,.heic"
                        onChange={(e) => setForm(p.id, { arquivo: e.target.files?.[0] })}
                        className="mt-1" data-testid={`mnf-arquivo-${p.id}`} />
                    </div>
                  </div>
                  <Button onClick={() => anexar(p.id)} disabled={a.anexando}
                    className="w-full mt-3 btn-sharp brand-bg text-white" data-testid={`mnf-anexar-${p.id}`}>
                    {a.anexando ? "Enviando…" : isRejeitada ? "📎 Reenviar NF corrigida" : "📎 Anexar NF"}
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
